import axios from 'axios';
import { Career, CreateCareerDto, UpdateCareerDto, CareerListResponse, CareerStatus } from '../types/career';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const careerService = {
  // Public endpoints
  getPublishedCareers: async (page = 1, limit = 10, search?: string): Promise<CareerListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.append('search', search);
    
    const response = await api.get(`/career/public?${params}`);
    return response.data;
  },

  getCareerBySlug: async (slug: string): Promise<Career> => {
    const response = await api.get(`/career/public/slug/${slug}`);
    return response.data;
  },

  getDepartments: async (): Promise<string[]> => {
    const response = await api.get('/career/public/departments');
    return response.data;
  },

  getLocations: async (): Promise<string[]> => {
    const response = await api.get('/career/public/locations');
    return response.data;
  },

  getEmploymentTypes: async (): Promise<string[]> => {
    const response = await api.get('/career/public/employment-types');
    return response.data;
  },

  getTags: async (): Promise<string[]> => {
    const response = await api.get('/career/public/tags');
    return response.data;
  },

  // Protected endpoints
  createCareer: async (careerData: CreateCareerDto): Promise<Career> => {
    const response = await api.post('/career', careerData);
    return response.data;
  },

  getAllCareers: async (
    page = 1,
    limit = 10,
    status?: CareerStatus,
    authorId?: string,
    search?: string
  ): Promise<CareerListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status) params.append('status', status);
    if (authorId) params.append('author', authorId);
    if (search) params.append('search', search);
    
    const response = await api.get(`/career?${params}`);
    return response.data;
  },

  getMyCareers: async (
    page = 1,
    limit = 10,
    status?: CareerStatus,
    search?: string
  ): Promise<CareerListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    
    const response = await api.get(`/career/my-careers?${params}`);
    return response.data;
  },

  getCareerById: async (id: string): Promise<Career> => {
    const response = await api.get(`/career/${id}`);
    return response.data;
  },

  updateCareer: async (id: string, careerData: UpdateCareerDto): Promise<Career> => {
    const response = await api.patch(`/career/${id}`, careerData);
    return response.data;
  },

  updateCareerStatus: async (id: string, status: CareerStatus): Promise<Career> => {
    const response = await api.patch(`/career/${id}`, { status });
    return response.data;
  },

  deleteCareer: async (id: string): Promise<void> => {
    await api.delete(`/career/${id}`);
  },

  getStats: async (): Promise<{
    total: number;
    published: number;
    draft: number;
    unpublished: number;
    totalViews: number;
    totalApplications: number;
  }> => {
    const response = await api.get('/career/stats');
    return response.data;
  },
}; 